import { useState } from 'react'

import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { useFormik } from 'formik'
import { Eye, EyeOff, Lock, ShieldCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { ROUTES } from '../../../constants'
import { useLanguage } from '../../../i18n'
import { resetPasswordApi } from '../api'
import { logout } from '../login/auth'

const inputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
  },
}

function ResetPassword() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [show, setShow] = useState({ next: false, confirm: false })
  const [submitError, setSubmitError] = useState('')
  const [success, setSuccess] = useState('')

  // Reset password mirrors the change-password flow: same Yup rules, same
  // mutation lifecycle, same platform header attachment. The only differences
  // are cosmetic (no "current password" field — the user is being forced to
  // reset, so we don't ask for what they may have forgotten) and the post-
  // success redirect: reset-password returns to login without flashing the
  // `passwordChanged` banner, since this *is* the password change the banner
  // was warning about.
  const resetPassword = useMutation({
    mutationFn: (payload) => resetPasswordApi(payload).then((res) => res.data),
    onSuccess: (body) => {
      const message = body?.status?.message || t('Password updated successfully')
      setSuccess(message)

      // Reset local state immediately so a back-nav doesn't see the
      // success alert with stale form values. Then drop the session:
      // tokens + cached user/branch payloads all have to go before the
      // user re-authenticates with their new password.
      formik.resetForm()
      logout()
      navigate(ROUTES.LOGIN, { replace: true })
    },
    onError: (err) => {
      setSubmitError(
        err.response?.data?.status?.message ||
          err.response?.data?.message ||
          err.message ||
          t('Failed to update password')
      )
    },
  })

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .min(8, t('Password must be at least 8 characters'))
      .matches(/[A-Z]/, t('Must contain at least one uppercase letter'))
      .matches(/[a-z]/, t('Must contain at least one lowercase letter'))
      .matches(/[0-9]/, t('Must contain at least one number'))
      .matches(/[^A-Za-z0-9]/, t('Must contain at least one special character'))
      .required(t('New password is required')),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], t('Passwords do not match'))
      .required(t('Please confirm new password')),
  })

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    validationSchema,
    onSubmit: (values) => {
      setSubmitError('')
      setSuccess('')
      resetPassword.mutate({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmNewPassword,
      })
    },
  })

  const fields = [
    {
      key: 'next',
      name: 'newPassword',
      label: t('New Password'),
      icon: <Lock size={18} />,
      visible: show.next,
      toggle: () => setShow((s) => ({ ...s, next: !s.next })),
    },
    {
      key: 'confirm',
      name: 'confirmNewPassword',
      label: t('Confirm New Password'),
      icon: <ShieldCheck size={18} />,
      visible: show.confirm,
      toggle: () => setShow((s) => ({ ...s, confirm: !s.confirm })),
    },
  ]

  const handleReset = () => {
    formik.resetForm()
    resetPassword.reset()
    setSubmitError('')
    setSuccess('')
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'var(--body-bg)',
        color: 'text.primary',
        p: 4,
      }}
    >
      <Stack spacing={1} sx={{ mb: 4, width: '100%', maxWidth: 560 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.18em' }}>
          {t('Account security')}
        </Typography>
        <Typography variant="h4" fontWeight={700} color="text.primary">
          {t('Reset Password')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
          {t(
            'Set a new password to regain access to your account. Use at least 8 characters including uppercase, lowercase, a number, and a special character.'
          )}
        </Typography>
      </Stack>

      <Paper
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 560,
        }}
      >
        {submitError && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2.5 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          sx={{ display: 'grid', gap: 2.5 }}
        >
          {fields.map((f) => {
            const touched = formik.touched[f.name]
            const error = formik.errors[f.name]
            return (
              <TextField
                key={f.key}
                label={f.label}
                name={f.name}
                value={formik.values[f.name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                autoComplete="new-password"
                type={f.visible ? 'text' : 'password'}
                sx={inputSx}
                error={Boolean(touched && error)}
                helperText={touched && error ? error : ' '}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{f.icon}</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={f.toggle}
                        edge="end"
                        aria-label={f.visible ? t('Hide password') : t('Show password')}
                        size="small"
                      >
                        {f.visible ? <EyeOff size={18} /> : <Eye size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )
          })}

          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={resetPassword.isPending}
              sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
              startIcon={
                resetPassword.isPending ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {resetPassword.isPending ? t('Updating…') : t('Update Password')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="large"
              disabled={resetPassword.isPending}
              sx={{ textTransform: 'none' }}
              onClick={handleReset}
            >
              {t('Reset')}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  )
}

export default ResetPassword
